import { Cell } from '@table-library/react-table-library/table';

import { formatMetric } from '../GscTools/format';
import { hourLabel } from './hours';

/**
 * The columns this report defines. None of them come from the shared GSC sets, because none of
 * the shared sets are keyed by an hour — and an hour is not a short day: it carries an offset
 * that has to stay visible, and a completeness state that every other report gets to assume.
 */

/**
 * The hour column, shown as the Pacific clock time with the exact key underneath.
 *
 * Both, deliberately. The clock time is what a reader is looking for; the full timestamp is what
 * distinguishes the two 01:00s on the day the clocks go back, and is what an export has to carry
 * for anyone to line a row up against a request. The export drops the label and keeps the key.
 */
function hourColumn(t) {
    return {
        label: t('tools.latest_hours.columns.hour'),
        key: 'key',
        data: 'key',
        // Sorted on the instant, not on the key. The keys are ISO strings carrying an offset, so
        // a text sort would put 01:00−08:00 before 01:00−07:00 — the wrong way round across a
        // daylight-saving fall-back — and a numeric sort on them is simply NaN.
        sortType: (array) => array.sort((a, b) => a.hour.valueOf() - b.hour.valueOf()),
        cellSize: 'minmax(220px, 2fr)',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return item.key;

            return (
                <span className="hour-cell">
                    <strong>{hourLabel(item.hour, { withDate: true })}</strong>
                    <small className="text-muted">UTC{item.hour.format('Z')}</small>
                </span>
            );
        },
    };
}

/** Whether the hour is settled, and if not, that we are assuming rather than being told. */
function stateColumn(t) {
    return {
        label: t('tools.latest_hours.columns.completeness'),
        key: 'provisional',
        data: 'provisional',
        sortType: (array) => array.sort((a, b) => Number(a.provisional) - Number(b.provisional)),
        cellSize: 'minmax(170px, 1.4fr)',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            const label = item.provisional
                ? t('tools.latest_hours.state.provisional')
                : t('tools.latest_hours.state.complete');

            if (csvDownload) return label;
            return item.provisional ? <span className="hour-provisional">{label}</span> : label;
        },
    };
}

const metricColumn = (metric, label, size) => ({
    label,
    key: metric,
    data: metric,
    sortType: 'number',
    cellSize: size,
    Component: Cell,
    renderCell: ({ data }) => formatMetric(metric, data),
});

/**
 * Block 03 — one row per hour Search Console returned, with its four metrics as returned.
 *
 * Every returned hour is here, provisional ones included and marked. Block 02 keeps them out of
 * its totals; this block is the evidence, and evidence that hides the rows a reader most wants to
 * check is not evidence. Sorting it never reorders the chart above, which stays chronological.
 */
export function evidenceColumns(t, { hasPosition }) {
    return [
        hourColumn(t),
        metricColumn('clicks', t('tools.gsc.metrics.clicks_short'), 'minmax(100px, 1fr)'),
        metricColumn('impressions', t('tools.gsc.metrics.impressions_short'), 'minmax(120px, 1fr)'),
        metricColumn('ctr', t('tools.gsc.metrics.ctr_short'), 'minmax(100px, 1fr)'),
        ...(hasPosition
            ? [
                  metricColumn(
                      'position',
                      t('tools.gsc.metrics.position_short'),
                      'minmax(120px, 1fr)',
                  ),
              ]
            : []),
        stateColumn(t),
    ];
}

/**
 * Block 02's accessible data table: the two plotted series, hour by hour.
 *
 * The prior column is dropped entirely when there is no comparison rather than filled with dashes
 * — an empty column reads as a comparison that failed instead of one that was never asked for.
 */
export function movementColumns(t, { metric, comparing }) {
    const metricName = t(`tools.gsc.metrics.${metric}_short`);

    return [
        hourColumn(t),
        {
            label: t('tools.latest_hours.columns.current_value', { metric: metricName }),
            key: 'currentValue',
            data: 'currentValue',
            sortType: 'number',
            cellSize: 'minmax(140px, 1.2fr)',
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                if (!item.returned) {
                    const label = t('tools.gsc.not_returned');
                    return csvDownload ? label : <span className="text-muted">{label}</span>;
                }
                return formatMetric(metric, item.currentValue);
            },
        },
        ...(comparing
            ? [
                  {
                      label: t('tools.latest_hours.columns.prior_value', { metric: metricName }),
                      key: 'priorValue',
                      data: 'priorValue',
                      sortType: 'number',
                      cellSize: 'minmax(170px, 1.4fr)',
                      Component: Cell,
                      renderCell: ({ item, csvDownload }) => {
                          if (!item.priorReturned) {
                              const label = t('tools.gsc.not_returned');
                              return csvDownload ? (
                                  label
                              ) : (
                                  <span className="text-muted">{label}</span>
                              );
                          }
                          return formatMetric(metric, item.priorValue);
                      },
                  },
              ]
            : []),
        stateColumn(t),
    ];
}

/** Block 01's boundary table, and block 04's actions table: both are label/value/value grids. */
export function boundaryColumns(t, { comparing }) {
    return [
        {
            label: t('tools.latest_hours.boundary.item'),
            key: 'item',
            data: 'item',
            sortType: 'string',
            cellSize: 'minmax(220px, 1.8fr)',
            Component: Cell,
        },
        {
            label: t('tools.latest_hours.boundary.current'),
            key: 'current',
            data: 'current',
            sortType: 'string',
            cellSize: 'minmax(230px, 2fr)',
            Component: Cell,
            renderCell: ({ data }) => data || '—',
        },
        ...(comparing
            ? [
                  {
                      label: t('tools.latest_hours.boundary.baseline'),
                      key: 'baseline',
                      data: 'baseline',
                      sortType: 'string',
                      cellSize: 'minmax(230px, 2fr)',
                      Component: Cell,
                      renderCell: ({ data }) => data || '—',
                  },
              ]
            : []),
    ];
}

export const ACTION_COLUMNS = (t) => [
    {
        label: t('tools.latest_hours.actions.action'),
        key: 'action',
        data: 'action',
        sortType: 'string',
        cellSize: 'minmax(220px, 1.6fr)',
        Component: Cell,
    },
    {
        label: t('tools.latest_hours.actions.behaviour'),
        key: 'behaviour',
        data: 'behaviour',
        sortType: 'string',
        cellSize: 'minmax(300px, 2.6fr)',
        Component: Cell,
    },
];
